
// src/config/api.js
export const BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  "https://stericab-backend.onrender.com";

const frToEn = (p) => {
  const s = String(p || "").toLowerCase();
  return s === "jour" ? "day" : s === "semaine" ? "week" : s === "mois" ? "month" : s;
};

console.log("🔧 API_BASE_URL:", BASE);
console.log("🔧 Environment:", process.env.NODE_ENV);

export const getToken = () => {
  try {
    return localStorage.getItem("token") || localStorage.getItem("jwt_token") || "";
  } catch { return ""; }
};

export const getAuthHeaders = () => {
  const token = getToken();
  const h = { "Content-Type": "application/json", "Accept": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h; // pas d'If-None-Match
};

export const API_ENDPOINTS = {
  // Auth
  LOGIN: () => `${BASE}/api/login`,
  // 👇 NEW
  BOOTSTRAP: (uid) => `${BASE}/api/bootstrap/${uid}`,

  // —— Stérilisation via /api/logs (PAS de route /api/sterilization ici) ——
  LOGS_STERILIZATION: (uid, fromYMD, toYMD, limit = 1000, offset = 0) =>
    `${BASE}/api/logs?user_id=${uid}&entity=sterilization_cycle&date_from=${fromYMD}&date_to=${toYMD}&limit=${limit}&offset=${offset}`,
  LOGS_STERI_OVERVIEW: (uid, tz='Europe/Paris') =>
    `${BASE}/api/logs/sterilization/overview/${uid}?tz=${encodeURIComponent(tz)}`,

  // Instruments
  INSTRUMENTS_LIST: (uid) => `${BASE}/api/instruments/list/${uid}`,

  // Tasks
  TASKS: (uid) => `${BASE}/api/tasks/${uid}`,
  TASKS_STATS: (uid) => `${BASE}/api/tasks/stats/${uid}`,
  TASKS_WEEKLY_CHART: (uid) => `${BASE}/api/tasks/weekly-chart/${uid}`,
  OPERATORS_TASKS_SUMMARY: (uid, pFr = "semaine") =>
    `${BASE}/api/operators/tasks-summary/${uid}?period=${frToEn(pFr)}`,
  
  // ---- Cursor feeds (Stericab backend) ----
  TASKS_FEED: ({ user_id, limit = 100, cursor, assigned_id } = {}) => {
    const q = new URLSearchParams();
    if (user_id) q.set('user_id', user_id);
    if (assigned_id) q.set('assigned_id', assigned_id);
    if (limit) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    return `${BASE}/api/tasks/feed?${q.toString()}`;
  },
  TASK_CHECKLIST_FEED: ({ task_id, limit = 200, cursor } = {}) => {
    const q = new URLSearchParams();
    if (task_id) q.set('task_id', task_id);
    if (limit) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    return `${BASE}/api/task-checklist/feed?${q.toString()}`;
  },
  TASK_INSTANCES_FEED: ({ user_id, task_id, status, date, limit = 100, cursor } = {}) => {
    const q = new URLSearchParams();
    if (user_id) q.set('user_id', user_id);
    if (task_id) q.set('task_id', task_id);
    if (status) q.set('status', status);
    if (date) q.set('date', date);
    if (limit) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    return `${BASE}/api/task-instances/feed?${q.toString()}`;
  },
  TASK_INSTANCE_CHECKLIST_FEED: ({ task_instance_id, limit = 200, cursor } = {}) => {
    const q = new URLSearchParams();
    if (task_instance_id) q.set('task_instance_id', task_instance_id);
    if (limit) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    return `${BASE}/api/task-instance-checklist/feed?${q.toString()}`;
  },
  
  // Operators
  OPERATORS: (uid) => `${BASE}/api/operators?user_id=${uid}`,
  OP_WEEKLY_HOURS_POST: (opId) => `${BASE}/api/operators/${opId}/weekly-hours`,
  OP_WEEKLY_HOURS_DELETE_DAY: (opId, weekday) => `${BASE}/api/operators/${opId}/weekly-hours/weekday/${weekday}`,
  // Instances du jour (pending)
  TASK_INSTANCES_TODAY_PENDING: (uid) => `${BASE}/api/task-instances/today-pending?user_id=${uid}`,
  
  // Attendance
  ATTENDANCE_DAYS: (from, to, userId) => {
    const enc = encodeURIComponent;
    let url = `${BASE}/api/attendance/days?from=${enc(from)}&to=${enc(to)}`;
    // 🔒 multi-tenant : toujours passer le user_id du compte connecté
    if (userId) url += `&user_id=${enc(userId)}`;
    return url;
  },
  ATTENDANCE_DAY_CREATE: () => `${BASE}/api/attendance/days`,
  ATTENDANCE_DAY_UPDATE: (id) => `${BASE}/api/attendance/days/${id}`,
  
  // Agenda / Schedule
  OP_SCHEDULE: (operatorId, from, to) => 
    `${BASE}/api/operators/${operatorId}/schedule?from=${from}&to=${to}`,
  
  OP_SCHEDULE_REBUILD: (operatorId, from, to) => 
    `${BASE}/api/operators/${operatorId}/schedule/rebuild?from=${from}&to=${to}`,
  
  OP_OVERRIDES: (operatorId, from, to) => 
    `${BASE}/api/operators/${operatorId}/overrides?from=${from}&to=${to}`,
  
  OP_OVERRIDES_BULK: (from, to) => 
    `${BASE}/api/operators/overrides/bulk?from=${from}&to=${to}`,
  
  OP_OVERRIDE_UPSERT: (operatorId, ymd) => 
    `${BASE}/api/operators/${operatorId}/overrides/${ymd}`,
  
  // Cabinets
  CABINETS_LIST: () => `${BASE}/api/cabinets`,
  CABINET_DETAIL: (id) => `${BASE}/api/cabinets/${id}`,
  
  // Distributor Accounts
  DISTRIBUTOR_ACCOUNTS_LIST: () => `${BASE}/api/distributor-accounts`,
  DISTRIBUTOR_ACCOUNT_DETAIL: (id) => `${BASE}/api/distributor-accounts/${id}`,
  DISTRIBUTOR_ACCOUNT_BY_CODE: (code) => `${BASE}/api/distributor-accounts/by-distributor/${code}`,
  
  // Employees
  EMPLOYEES_LIST: (operatorId = null) => {
    const url = `${BASE}/api/employees`;
    return operatorId ? `${url}?operator_id=${operatorId}` : url;
  },
  EMPLOYEE_DETAIL: (id) => `${BASE}/api/employees/${id}`,
  
  // School schedules
  SCHOOL_LIST: () => `${BASE}/api/school`,
  SCHOOL_DETAIL: (operatorId) => `${BASE}/api/school/${operatorId}`,
  
  // Payroll
  PAYROLL_CREATE: () => `${BASE}/api/payroll`,
  PAYROLL_LIST: () => `${BASE}/api/payroll`,
  PAYROLL_DETAIL: (id) => `${BASE}/api/payroll/${id}`,

  // ===== Agenda Live (SSE + feed fallback) =====
  CHANGES_SSE: (channel = 'agenda') => `${BASE}/api/logs/stream?channel=${encodeURIComponent(channel)}`,

  // fallback polling (cursor feed des logs)
  CHANGES_FEED: ({ channel = 'agenda', cursor, limit = 200 } = {}) => {
    const q = new URLSearchParams();
    q.set('channel', channel);
    if (cursor) q.set('cursor', cursor);
    if (limit) q.set('limit', String(limit));
    return `${BASE}/api/logs/feed?${q.toString()}`;
  },

  // Overlaps agenda data feeds
  OPS_LIST: () => `${BASE}/api/operators`,
  OP_WEEKLY_HOURS: () => `${BASE}/api/operators/weekly-hours`,

  // Catalogue & Commandes
  CATALOGUE_HEALTH: () => `${BASE}/api/catalogue/health`,
  CATALOGUE_PRODUCTS: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.brand) q.set('brand', params.brand);
    if (params.category) q.set('category', params.category);
    if (params.subcategory) q.set('subcategory', params.subcategory);
    if (params.has_price !== undefined) q.set('has_price', params.has_price);
    if (params.sort) q.set('sort', params.sort);
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    return `${BASE}/api/catalogue/products?${q.toString()}`;
  },
  CATALOGUE_PRODUCT_DETAIL: (id) => `${BASE}/api/catalogue/products/${id}`,
  CATALOGUE_VARIANT_DETAIL: (id) => `${BASE}/api/catalogue/variants/${id}`,
  CATALOGUE_DISTRIBUTORS: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    return `${BASE}/api/catalogue/distributors?${q.toString()}`;
  },
  CATALOGUE_PRICES: (productId, params = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return `${BASE}/api/catalogue/prices/${productId}?${q.toString()}`;
  },

  // Commandes (authentifiées)
  COMMANDES_HEALTH: () => `${BASE}/api/commandes/health`,
  COMMANDES_LIST: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.product_id) q.set('product_id', params.product_id);
    if (params.variant_id) q.set('variant_id', params.variant_id);
    return `${BASE}/api/commandes?${q.toString()}`;
  },
  COMMANDES_DETAIL: (id) => `${BASE}/api/commandes/${id}`,
  COMMANDES_CREATE: () => `${BASE}/api/commandes/`,
  COMMANDES_CANCEL: (id) => `${BASE}/api/commandes/${id}/cancel`,
  COMMANDES_INVOICES: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    return `${BASE}/api/commandes/factures?${q.toString()}`;
  },
  COMMANDES_INVOICE_CREATE: () => `${BASE}/api/commandes/invoice`,
  COMMANDES_INVOICE_UPLOAD: () => `${BASE}/api/commandes/invoice/upload`,
  COMMANDES_LATEST_PRICES: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    return `${BASE}/api/commandes/latest-prices?${q.toString()}`;
  },
  COMMANDES_PURCHASED_ITEMS: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.category) q.set('category', params.category);
    if (params.distributor) q.set('distributor', params.distributor);
    return `${BASE}/api/commandes/purchased-items?${q.toString()}`;
  },
  
  // Statistiques de dépenses
  COMMANDES_STATS_MONTHLY_SPEND: (params = {}) => {
    const q = new URLSearchParams();
    if (params.months) q.set('months', params.months);
    return `${BASE}/api/commandes/stats/monthly-spend?${q.toString()}`;
  },
  COMMANDES_STATS_SPEND_BY_CATEGORY: (params = {}) => {
    const q = new URLSearchParams();
    if (params.months) q.set('months', params.months);
    if (params.status) q.set('status', params.status);
    return `${BASE}/api/commandes/stats/spend-by-category?${q.toString()}`;
  },
  COMMANDES_STATS_PRICE_ALERTS: (params = {}) => {
    const q = new URLSearchParams();
    if (params.min_increase_percent) q.set('min_increase_percent', params.min_increase_percent);
    if (params.min_increase_amount) q.set('min_increase_amount', params.min_increase_amount);
    if (params.months) q.set('months', params.months);
    return `${BASE}/api/commandes/stats/price-alerts?${q.toString()}`;
  },
  COMMANDES_STATS_PRICE_HISTORY: (params = {}) => {
    const q = new URLSearchParams();
    if (params.product_id) q.set('product_id', params.product_id);
    if (params.months) q.set('months', params.months);
    return `${BASE}/api/commandes/stats/price-history?${q.toString()}`;
  },
};